// Merchant request: show search suggestions directly below the sidebar/header search box while typing.
// Progressive enhancement over the plain GET form in snippets/header-search.liquid.
class SHeaderSuggest extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-suggest-panel]');
    if (!this.input || !this.panel) return;
    this.timer = null;
    this.controller = null;
    this.input.setAttribute('autocomplete', 'off');
    this.input.addEventListener('input', () => this.schedule());
    this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.schedule(); });
    this.input.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) setTimeout(() => this.close(), 120); });
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.fetchResults(), 200);
  }

  // Before typing, the popular keywords are shown by CSS (focus-within) in header-search.liquid.
  showInitial() {
    this.close();
  }

  close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  formatPrice(value) {
    const amount = Number(value);
    if (Number.isNaN(amount)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ja', { style: 'currency', currency: this.dataset.currency || 'JPY' }).format(amount);
    } catch (_) {
      return amount.toLocaleString();
    }
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
  }

  async fetchResults() {
    const q = this.input.value.trim();
    if (!q) { if (this.controller) this.controller.abort(); this.showInitial(); return; }
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const url = `${this.dataset.url}.json?q=${encodeURIComponent(q)}&resources[type]=product,query&resources[limit]=6&resources[limit_scope]=each`;
    try {
      const res = await fetch(url, { signal: this.controller.signal });
      if (!res.ok) return;
      const data = await res.json();
      this.render(q, data?.resources?.results || {});
    } catch (err) {
      if (err.name !== 'AbortError') this.close();
    }
  }

  render(q, results) {
    const products = results.products || [];
    const queries = (results.queries || []).filter((item) => item.text && item.text.toLowerCase() !== q.toLowerCase()).slice(0, 4);
    const searchUrl = `${this.dataset.searchUrl}?q=${encodeURIComponent(q)}`;
    let html = '';

    if (queries.length) {
      html += '<ul role="list" class="flex flex-col py-1">';
      queries.forEach((item) => {
        html += `<li><a href="${this.escape(item.url)}" class="flex items-center gap-2 px-3 py-2 text-sm text-on-surface no-underline hover:bg-sidebar">${this.escape(item.text)}</a></li>`;
      });
      html += '</ul>';
    }

    if (products.length) {
      html += `<p class="border-t border-border px-3 pt-3 pb-1 text-xs tracking-[0.12em] text-on-surface-secondary">${this.escape(this.dataset.productsLabel)}</p><ul role="list" class="flex flex-col pb-1">`;
      products.forEach((p) => {
        const img = p.featured_image?.url || p.image || '';
        const imgUrl = img ? `${img}${img.includes('?') ? '&' : '?'}width=96` : '';
        html += `<li><a href="${this.escape(p.url)}" class="flex items-center gap-3 px-3 py-2 text-on-surface no-underline hover:bg-sidebar">`
          + (imgUrl ? `<img src="${this.escape(imgUrl)}" alt="" width="48" height="48" loading="lazy" class="size-12 shrink-0 object-cover bg-sidebar">` : '<span class="size-12 shrink-0 bg-sidebar"></span>')
          + `<span class="flex min-w-0 flex-col gap-0.5"><span class="line-clamp-2 text-[13px] leading-[1.5]">${this.escape(p.title)}</span><span class="text-xs text-on-surface-secondary">${this.escape(this.formatPrice(p.price))}</span></span></a></li>`;
      });
      html += '</ul>';
    }

    if (!queries.length && !products.length) {
      html += `<p class="px-3 py-4 text-sm text-on-surface-secondary">${this.escape(this.dataset.emptyLabel.replace('__TERMS__', q))}</p>`;
    } else {
      html += `<a href="${this.escape(searchUrl)}" class="block border-t border-border px-3 py-3 text-center text-[13px] tracking-[0.06em] text-on-surface no-underline hover:bg-sidebar">${this.escape(this.dataset.viewAllLabel)}</a>`;
    }

    this.panel.innerHTML = html;
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }
}

if (!customElements.get('s-header-suggest')) customElements.define('s-header-suggest', SHeaderSuggest);
